import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash, Search, Filter, X, Users, Info, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RefreshCw, Calendar, User, Phone, MapPin, FileText, Save, AlertCircle, Eye, Upload, Download, FileSpreadsheet, CheckCircle, XCircle, IdCard, Briefcase, Link2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { EMPLOYEE_WORK_STATUSES, DEFAULT_EMPLOYEE_WORK_STATUS, getEmployeeWorkStatus } from '../payroll/core/domain';
import { findPayrollUsage } from '../payroll/data/employeeUsage';
import { syncEmployeeTeams } from '../payroll/data/employeeTeams';
import { splitEmployeeTeams } from '../payroll/core/periodInputs';

const EMPTY_NHAN_SU = {
    'SỐ HĐ': '',
    'HỌ VÀ TÊN': '',
    'TỔ': '',
    'BỘ PHẬN': '',
    'SỐ ĐIỆN THOẠI': '',
    'NGÀY SINH': '',
    'GIỚI TÍNH': 'Nam',
    'CCCD': '',
    'NGÀY CẤP': '',
    'NGÀY HẾT HẠN CCCD': '',
    'NƠI CẤP': '',
    'QUÊ QUÁN': '',
    'ĐỊA CHỈ': '',
    'TRẠNG THÁI LÀM VIỆC': DEFAULT_EMPLOYEE_WORK_STATUS,
    'NGÀY BẮT ĐẦU': '',
    'NGÀY NGHỈ': ''
};

const WORK_STATUS_BADGE = {
    'Đang làm': 'bg-green-100 text-green-800',
    'Tạm nghỉ': 'bg-yellow-100 text-yellow-800',
    'Đã nghỉ': 'bg-gray-200 text-gray-700'
};

// Bảng lương lấy nhân sự theo khoảng làm việc, nên ngày nghỉ phải hợp lệ thì mới lọc đúng người.
const validateWorkPeriod = (trangThai, ngayBatDau, ngayNghi) => {
    const errors = [];
    if (!EMPLOYEE_WORK_STATUSES.includes(trangThai)) {
        errors.push(`Trạng thái làm việc phải là: ${EMPLOYEE_WORK_STATUSES.join(', ')}`);
    }
    if (trangThai === 'Đã nghỉ' && !ngayNghi) {
        errors.push('Nhân sự đã nghỉ phải có ngày nghỉ');
    }
    if (ngayBatDau && ngayNghi && new Date(ngayNghi) < new Date(ngayBatDau)) {
        errors.push('Ngày nghỉ không được trước ngày bắt đầu');
    }
    return errors;
};

const NhanSuManagement = () => {
    // State Management
    const [nhanSuList, setNhanSuList] = useState([]);
    const [phanBoList, setPhanBoList] = useState([]);
    const [currentNhanSu, setCurrentNhanSu] = useState(EMPTY_NHAN_SU);

    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [nhanSuToDelete, setNhanSuToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isEditMode, setIsEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [originalSoHD, setOriginalSoHD] = useState('');
    const [isSyncingTeams, setIsSyncingTeams] = useState(false);

    // Import/Export states
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState([]);
    const [isImporting, setIsImporting] = useState(false);

    const fileInputRef = useRef(null);

    // Filter states
    const [filterBoPhan, setFilterBoPhan] = useState('ALL');
    const [filterTo, setFilterTo] = useState('ALL');
    const [filterGioiTinh, setFilterGioiTinh] = useState('ALL');
    const [filterTrangThai, setFilterTrangThai] = useState('ALL');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // ==================== FETCH FUNCTIONS ====================

    const fetchNhanSuList = async () => {
        try {
            setIsLoading(true);
            const response = await authUtils.apiRequest('NHANSU', 'Find', {});
            setNhanSuList(response);
        } catch (error) {
            console.error('Error fetching nhan su list:', error);
            toast.error('Lỗi khi tải danh sách nhân sự');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPhanBoList = async () => {
        try {
            const response = await authUtils.apiRequest('TO_PBNS', 'Find', {});
            setPhanBoList(response);
        } catch (error) {
            console.error('Error fetching phan bo list:', error);
        }
    };

    useEffect(() => {
        fetchNhanSuList();
        fetchPhanBoList();
    }, []);

    // ==================== EXCEL FUNCTIONS ====================

    const convertDateDDMMYYYYtoYYYYMMDD = (dateStr) => {
        if (!dateStr) return '';

        const dateString = dateStr.toString().trim();

        if (dateString.includes('-') && dateString.split('-')[0].length === 4) {
            return dateString;
        }

        const parts = dateString.split('/');
        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }

        return '';
    };

    const convertDateYYYYMMDDtoDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const handleExportTemplate = () => {
        const templateData = [
            {
                'Số HĐ': 'HD-2024-001',
                'Họ và Tên': 'Nguyễn Văn A',
                'Bộ Phận': 'Sản xuất',
                'Số Điện Thoại': '0901234567',
                'Ngày Sinh (DD/MM/YYYY)': '01/01/1990',
                'Giới Tính': 'Nam',
                'CCCD': '001234567890',
                'Ngày Cấp (DD/MM/YYYY)': '01/01/2020',
                'Ngày Hết Hạn CCCD (DD/MM/YYYY)': '01/01/2030',
                'Nơi Cấp': 'Công an TP.HCM',
                'Quê Quán': 'TP.HCM',
                'Địa Chỉ': '123 Nguyễn Văn Linh, Q.7, TP.HCM',
                'Trạng Thái Làm Việc': 'Đang làm',
                'Ngày Bắt Đầu (DD/MM/YYYY)': '01/03/2024',
                'Ngày Nghỉ (DD/MM/YYYY)': ''
            },
            {
                'Số HĐ': 'HD-2024-002',
                'Họ và Tên': 'Trần Thị B',
                'Bộ Phận': 'Kế toán',
                'Số Điện Thoại': '0912345678',
                'Ngày Sinh (DD/MM/YYYY)': '15/05/1995',
                'Giới Tính': 'Nữ',
                'CCCD': '001234567891',
                'Ngày Cấp (DD/MM/YYYY)': '15/05/2021',
                'Ngày Hết Hạn CCCD (DD/MM/YYYY)': '15/05/2031',
                'Nơi Cấp': 'Công an Bình Dương',
                'Quê Quán': 'Bình Dương',
                'Địa Chỉ': '456 Đại lộ Bình Dương, Thủ Dầu Một',
                'Trạng Thái Làm Việc': 'Đã nghỉ',
                'Ngày Bắt Đầu (DD/MM/YYYY)': '10/01/2023',
                'Ngày Nghỉ (DD/MM/YYYY)': '30/06/2025'
            }
        ];

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(templateData);

        ws['!cols'] = [
            { wch: 15 }, // Số HĐ
            { wch: 25 }, // Họ và Tên
            { wch: 15 }, // Bộ Phận
            { wch: 15 }, // SĐT
            { wch: 20 }, // Ngày Sinh
            { wch: 12 }, // Giới Tính
            { wch: 15 }, // CCCD
            { wch: 20 }, // Ngày Cấp
            { wch: 25 }, // Ngày Hết Hạn
            { wch: 20 }, // Nơi Cấp
            { wch: 20 }, // Quê Quán
            { wch: 40 }, // Địa Chỉ
            { wch: 20 }, // Trạng Thái Làm Việc
            { wch: 25 }, // Ngày Bắt Đầu
            { wch: 25 }  // Ngày Nghỉ
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Mẫu Nhân Sự');

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, `Template_NhanSu_${new Date().getTime()}.xlsx`);

        toast.success('Đã tải xuống file mẫu Excel');
    };

    const handleExportData = () => {
        if (nhanSuList.length === 0) {
            toast.warning('Không có dữ liệu để xuất');
            return;
        }

        const exportData = nhanSuList.map(ns => ({
            'Số HĐ': ns['SỐ HĐ'],
            'Họ và Tên': ns['HỌ VÀ TÊN'],
            'Tổ': ns['TỔ'],
            'Bộ Phận': ns['BỘ PHẬN'],
            'Số Điện Thoại': ns['SỐ ĐIỆN THOẠI'],
            'Ngày Sinh': ns['NGÀY SINH'] ? convertDateYYYYMMDDtoDDMMYYYY(ns['NGÀY SINH']) : '',
            'Giới Tính': ns['GIỚI TÍNH'],
            'CCCD': ns['CCCD'],
            'Ngày Cấp': ns['NGÀY CẤP'] ? convertDateYYYYMMDDtoDDMMYYYY(ns['NGÀY CẤP']) : '',
            'Ngày Hết Hạn CCCD': ns['NGÀY HẾT HẠN CCCD'] ? convertDateYYYYMMDDtoDDMMYYYY(ns['NGÀY HẾT HẠN CCCD']) : '',
            'Nơi Cấp': ns['NƠI CẤP'],
            'Quê Quán': ns['QUÊ QUÁN'],
            'Địa Chỉ': ns['ĐỊA CHỈ'],
            'Trạng Thái Làm Việc': getEmployeeWorkStatus(ns),
            'Ngày Bắt Đầu': ns['NGÀY BẮT ĐẦU'] ? convertDateYYYYMMDDtoDDMMYYYY(ns['NGÀY BẮT ĐẦU']) : '',
            'Ngày Nghỉ': ns['NGÀY NGHỈ'] ? convertDateYYYYMMDDtoDDMMYYYY(ns['NGÀY NGHỈ']) : ''
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);

        ws['!cols'] = [
            { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 15 },
            { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
            { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 40 },
            { wch: 20 }, { wch: 15 }, { wch: 15 }
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Danh Sách Nhân Sự');

        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, `DanhSach_NhanSu_${new Date().getTime()}.xlsx`);

        toast.success('Đã xuất dữ liệu ra Excel');
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Vui lòng chọn file Excel (.xlsx hoặc .xls)');
            return;
        }

        setImportFile(file);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const sheetName = wb.SheetNames[0];
                const ws = wb.Sheets[sheetName];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    toast.error('File Excel không có dữ liệu');
                    return;
                }

                console.log(`📊 Đọc được ${data.length} dòng từ sheet "${sheetName}"`);

                const processedData = data.map((row, index) => {
                    let processedRow = {
                        rowIndex: index + 2,
                        soHD: row['Số HĐ'] || '',
                        hoTen: row['Họ và Tên'] || '',
                        boPhan: row['Bộ Phận'] || '',
                        sdt: row['Số Điện Thoại'] || '',
                        ngaySinh: row['Ngày Sinh (DD/MM/YYYY)'] || row['Ngày Sinh'] || '',
                        gioiTinh: row['Giới Tính'] || 'Nam',
                        cccd: row['CCCD'] || '',
                        ngayCap: row['Ngày Cấp (DD/MM/YYYY)'] || row['Ngày Cấp'] || '',
                        ngayHetHan: row['Ngày Hết Hạn CCCD (DD/MM/YYYY)'] || row['Ngày Hết Hạn CCCD'] || '',
                        noiCap: row['Nơi Cấp'] || '',
                        queQuan: row['Quê Quán'] || '',
                        diaChi: row['Địa Chỉ'] || '',
                        trangThai: String(row['Trạng Thái Làm Việc'] || '').trim() || DEFAULT_EMPLOYEE_WORK_STATUS,
                        ngayBatDau: row['Ngày Bắt Đầu (DD/MM/YYYY)'] || row['Ngày Bắt Đầu'] || '',
                        ngayNghi: row['Ngày Nghỉ (DD/MM/YYYY)'] || row['Ngày Nghỉ'] || '',
                        errors: []
                    };

                    // Validation
                    if (!processedRow.soHD || String(processedRow.soHD).trim() === '') {
                        processedRow.errors.push('Thiếu Số HĐ');
                    } else {
                        const soHDClean = String(processedRow.soHD).trim();

                        // Kiểm tra trùng trong file
                        const isDuplicateInImport = data.slice(0, index).some(prevRow =>
                            String(prevRow['Số HĐ'] || '').trim() === soHDClean
                        );
                        if (isDuplicateInImport) {
                            processedRow.errors.push(`Số HĐ ${soHDClean} bị trùng trong file Excel`);
                        }

                        // Kiểm tra trùng trong database
                        const isDuplicateInDB = nhanSuList.some(ns => ns['SỐ HĐ'] === soHDClean);
                        if (isDuplicateInDB) {
                            processedRow.errors.push(`Số HĐ ${soHDClean} đã tồn tại trong hệ thống`);
                        }
                    }

                    if (!processedRow.hoTen) {
                        processedRow.errors.push('Thiếu Họ và Tên');
                    }

                    // Validate số điện thoại
                    if (processedRow.sdt) {
                        const sdtClean = String(processedRow.sdt).replace(/\D/g, '');
                        if (sdtClean.length < 10 || sdtClean.length > 11) {
                            processedRow.errors.push('Số điện thoại không hợp lệ');
                        }
                    }

                    // Validate CCCD
                    if (processedRow.cccd) {
                        const cccdClean = String(processedRow.cccd).replace(/\D/g, '');
                        if (cccdClean.length !== 12) {
                            processedRow.errors.push('CCCD phải có 12 số');
                        }
                    }

                    processedRow.errors.push(...validateWorkPeriod(
                        processedRow.trangThai,
                        convertDateDDMMYYYYtoYYYYMMDD(processedRow.ngayBatDau),
                        convertDateDDMMYYYYtoYYYYMMDD(processedRow.ngayNghi)
                    ));

                    return processedRow;
                });

                setImportPreview(processedData);

                const validCount = processedData.filter(r => r.errors.length === 0).length;
                const errorCount = processedData.filter(r => r.errors.length > 0).length;

                if (validCount > 0) {
                    toast.success(`Đã đọc ${processedData.length} dòng: ${validCount} hợp lệ, ${errorCount} lỗi`);
                } else {
                    toast.error('Không có dòng dữ liệu hợp lệ');
                }
            } catch (error) {
                console.error('❌ Error reading Excel file:', error);
                toast.error('Có lỗi khi đọc file Excel: ' + error.message);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleImportData = async () => {
        if (importPreview.length === 0) {
            toast.error('Không có dữ liệu để import');
            return;
        }

        const validRows = importPreview.filter(row => row.errors.length === 0);
        if (validRows.length === 0) {
            toast.error('Không có dòng dữ liệu hợp lệ để import');
            return;
        }

        const hasErrors = importPreview.some(row => row.errors.length > 0);
        if (hasErrors) {
            const confirmImport = window.confirm(
                `Có ${importPreview.length - validRows.length} dòng lỗi. Bạn có muốn import ${validRows.length} dòng hợp lệ không?`
            );
            if (!confirmImport) return;
        }

        try {
            setIsImporting(true);

            const rowsToImport = validRows.map(row => ({
                'SỐ HĐ': String(row.soHD).trim(),
                'HỌ VÀ TÊN': row.hoTen,
                'BỘ PHẬN': row.boPhan,
                'SỐ ĐIỆN THOẠI': row.sdt,
                'NGÀY SINH': convertDateDDMMYYYYtoYYYYMMDD(row.ngaySinh),
                'GIỚI TÍNH': row.gioiTinh,
                'CCCD': row.cccd,
                'NGÀY CẤP': convertDateDDMMYYYYtoYYYYMMDD(row.ngayCap),
                'NGÀY HẾT HẠN CCCD': convertDateDDMMYYYYtoYYYYMMDD(row.ngayHetHan),
                'NƠI CẤP': row.noiCap,
                'QUÊ QUÁN': row.queQuan,
                'ĐỊA CHỈ': row.diaChi,
                'TRẠNG THÁI LÀM VIỆC': row.trangThai,
                'NGÀY BẮT ĐẦU': convertDateDDMMYYYYtoYYYYMMDD(row.ngayBatDau),
                'NGÀY NGHỈ': convertDateDDMMYYYYtoYYYYMMDD(row.ngayNghi)
            }));

            await authUtils.apiRequest('NHANSU', 'Add', {
                "Rows": rowsToImport
            });

            toast.success(`Import thành công ${validRows.length} nhân sự`);
            await fetchNhanSuList();
            handleCloseImportModal();

        } catch (error) {
            console.error('Error importing data:', error);
            toast.error('Có lỗi xảy ra khi import dữ liệu: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleOpenImportModal = () => {
        setShowImportModal(true);
        setImportFile(null);
        setImportPreview([]);
    };

    const handleCloseImportModal = () => {
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ==================== FORMAT FUNCTIONS ====================

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('vi-VN');
        } catch (error) {
            return dateString;
        }
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return '';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    // ==================== MODAL FUNCTIONS ====================

    const handleOpenModal = (nhanSu = null) => {
        if (nhanSu) {
            setIsEditMode(true);
            setOriginalSoHD(nhanSu['SỐ HĐ']);
            setCurrentNhanSu({
                'SỐ HĐ': nhanSu['SỐ HĐ'] || '',
                'HỌ VÀ TÊN': nhanSu['HỌ VÀ TÊN'] || '',
                'TỔ': nhanSu['TỔ'] || '',
                'BỘ PHẬN': nhanSu['BỘ PHẬN'] || '',
                'SỐ ĐIỆN THOẠI': nhanSu['SỐ ĐIỆN THOẠI'] || '',
                'NGÀY SINH': nhanSu['NGÀY SINH'] ? new Date(nhanSu['NGÀY SINH']).toISOString().split('T')[0] : '',
                'GIỚI TÍNH': nhanSu['GIỚI TÍNH'] || 'Nam',
                'CCCD': nhanSu['CCCD'] || '',
                'NGÀY CẤP': nhanSu['NGÀY CẤP'] ? new Date(nhanSu['NGÀY CẤP']).toISOString().split('T')[0] : '',
                'NGÀY HẾT HẠN CCCD': nhanSu['NGÀY HẾT HẠN CCCD'] ? new Date(nhanSu['NGÀY HẾT HẠN CCCD']).toISOString().split('T')[0] : '',
                'NƠI CẤP': nhanSu['NƠI CẤP'] || '',
                'QUÊ QUÁN': nhanSu['QUÊ QUÁN'] || '',
                'ĐỊA CHỈ': nhanSu['ĐỊA CHỈ'] || '',
                'TRẠNG THÁI LÀM VIỆC': getEmployeeWorkStatus(nhanSu),
                'NGÀY BẮT ĐẦU': nhanSu['NGÀY BẮT ĐẦU'] ? new Date(nhanSu['NGÀY BẮT ĐẦU']).toISOString().split('T')[0] : '',
                'NGÀY NGHỈ': nhanSu['NGÀY NGHỈ'] ? new Date(nhanSu['NGÀY NGHỈ']).toISOString().split('T')[0] : ''
            });
        } else {
            setIsEditMode(false);
            setOriginalSoHD('');
            setCurrentNhanSu(EMPTY_NHAN_SU);
        }
        setShowModal(true);
    };

    const handleOpenDetailModal = (nhanSu) => {
        setCurrentNhanSu(nhanSu);
        setShowDetailModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setShowDetailModal(false);
        setIsEditMode(false);
        setOriginalSoHD('');
        setCurrentNhanSu(EMPTY_NHAN_SU);
    };

    const handleInputChange = (field, value) => {
        setCurrentNhanSu(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateNhanSu = (nhanSu) => {
        const errors = [];

        if (!nhanSu['SỐ HĐ'] || nhanSu['SỐ HĐ'].trim() === '') {
            errors.push('Số HĐ không được để trống');
        }

        if (!nhanSu['HỌ VÀ TÊN'] || nhanSu['HỌ VÀ TÊN'].trim() === '') {
            errors.push('Họ và tên không được để trống');
        }

        if (nhanSu['SỐ ĐIỆN THOẠI']) {
            const sdtClean = nhanSu['SỐ ĐIỆN THOẠI'].replace(/\D/g, '');
            if (sdtClean.length < 10 || sdtClean.length > 11) {
                errors.push('Số điện thoại không hợp lệ (10-11 số)');
            }
        }

        if (nhanSu['CCCD']) {
            const cccdClean = nhanSu['CCCD'].replace(/\D/g, '');
            if (cccdClean.length !== 12) {
                errors.push('CCCD phải có 12 số');
            }
        }

        errors.push(...validateWorkPeriod(nhanSu['TRẠNG THÁI LÀM VIỆC'], nhanSu['NGÀY BẮT ĐẦU'], nhanSu['NGÀY NGHỈ']));

        return errors;
    };

    // Trả về thông báo lỗi nếu SỐ HĐ đã có dữ liệu lương (hoặc không kiểm tra được), null nếu được phép.
    const checkPayrollLock = async (soHd, action) => {
        try {
            const usage = await findPayrollUsage(soHd);
            return usage.length
                ? `Không thể ${action} vì Số HĐ ${soHd} đã có ${usage.join(', ')}.`
                : null;
        } catch (error) {
            console.error('Error checking payroll usage:', error);
            return `Không kiểm tra được dữ liệu lương của Số HĐ ${soHd} nên chưa thể ${action}.`;
        }
    };

    const handleSaveNhanSu = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const errors = validateNhanSu(currentNhanSu);
            if (errors.length > 0) {
                toast.error(errors.join('\n'));
                setIsSubmitting(false);
                return;
            }

            const nhanSuToSave = { ...currentNhanSu };

            if (isEditMode) {
                if (originalSoHD !== nhanSuToSave['SỐ HĐ']) {
                    const existingNhanSu = nhanSuList.find(
                        ns => ns['SỐ HĐ'] === nhanSuToSave['SỐ HĐ']
                    );

                    if (existingNhanSu) {
                        toast.error('Số HĐ mới này đã tồn tại!');
                        setIsSubmitting(false);
                        return;
                    }

                    const lockMessage = await checkPayrollLock(originalSoHD, 'đổi Số HĐ');
                    if (lockMessage) {
                        toast.error(lockMessage);
                        setIsSubmitting(false);
                        return;
                    }

                    await authUtils.apiRequest('NHANSU', 'Delete', {
                        "Rows": [{ "SỐ HĐ": originalSoHD }]
                    });

                    await authUtils.apiRequest('NHANSU', 'Add', {
                        "Rows": [nhanSuToSave]
                    });

                    toast.success('Cập nhật nhân sự thành công!');
                } else {
                    await authUtils.apiRequest('NHANSU', 'Edit', {
                        "Rows": [nhanSuToSave]
                    });

                    toast.success('Cập nhật nhân sự thành công!');
                }
            } else {
                const existingNhanSu = nhanSuList.find(
                    ns => ns['SỐ HĐ'] === nhanSuToSave['SỐ HĐ']
                );

                if (existingNhanSu) {
                    toast.error('Số HĐ này đã tồn tại!');
                    setIsSubmitting(false);
                    return;
                }

                await authUtils.apiRequest('NHANSU', 'Add', {
                    "Rows": [nhanSuToSave]
                });

                toast.success('Thêm nhân sự mới thành công!');
            }

            await fetchNhanSuList();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving nhan su:', error);
            toast.error('Có lỗi xảy ra: ' + (error.message || 'Không thể lưu nhân sự'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenDeleteConfirmation = (nhanSu) => {
        setNhanSuToDelete(nhanSu);
        setShowDeleteConfirmation(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setShowDeleteConfirmation(false);
        setNhanSuToDelete(null);
    };

    const handleDeleteNhanSu = async () => {
        if (!nhanSuToDelete) return;

        try {
            const lockMessage = await checkPayrollLock(nhanSuToDelete['SỐ HĐ'], 'xóa nhân sự');
            if (lockMessage) {
                toast.error(lockMessage);
                return;
            }

            await authUtils.apiRequest('NHANSU', 'Delete', {
                "Rows": [{ "SỐ HĐ": nhanSuToDelete['SỐ HĐ'] }]
            });

            toast.success('Xóa nhân sự thành công!');
            await fetchNhanSuList();
            handleCloseDeleteConfirmation();
        } catch (error) {
            console.error('Error deleting nhan su:', error);
            toast.error('Có lỗi xảy ra khi xóa nhân sự: ' + (error.message || ''));
        }
    };

    // ==================== SORT & FILTER ====================

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortedNhanSuList = useCallback(() => {
        const sortableList = [...nhanSuList];
        if (sortConfig.key) {
            sortableList.sort((a, b) => {
                const keyA = a[sortConfig.key] || '';
                const keyB = b[sortConfig.key] || '';

                if (sortConfig.key === 'NGÀY SINH' || sortConfig.key === 'NGÀY CẤP' || sortConfig.key === 'NGÀY HẾT HẠN CCCD') {
                    const dateA = new Date(keyA);
                    const dateB = new Date(keyB);
                    return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
                }

                if (keyA < keyB) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (keyA > keyB) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableList;
    }, [nhanSuList, sortConfig]);

    const filteredNhanSuList = getSortedNhanSuList().filter(ns => {
        const searchLower = search.toLowerCase();
        const matchesSearch = (
            ns['SỐ HĐ']?.toLowerCase().includes(searchLower) ||
            ns['HỌ VÀ TÊN']?.toLowerCase().includes(searchLower) ||
            ns['SỐ ĐIỆN THOẠI']?.toLowerCase().includes(searchLower) ||
            ns['CCCD']?.toLowerCase().includes(searchLower)
        );

        const matchesBoPhan = filterBoPhan === 'ALL' || ns['BỘ PHẬN'] === filterBoPhan;
        const matchesTo = filterTo === 'ALL' || splitEmployeeTeams(ns['TỔ']).includes(filterTo);
        const matchesGioiTinh = filterGioiTinh === 'ALL' || ns['GIỚI TÍNH'] === filterGioiTinh;
        const matchesTrangThai = filterTrangThai === 'ALL' || getEmployeeWorkStatus(ns) === filterTrangThai;

        return matchesSearch && matchesBoPhan && matchesTo && matchesGioiTinh && matchesTrangThai;
    });

    // ==================== PAGINATION ====================

    const totalPages = Math.ceil(filteredNhanSuList.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredNhanSuList.slice(indexOfFirstItem, indexOfLastItem);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, filterBoPhan, filterTo, filterGioiTinh, filterTrangThai, itemsPerPage]);

    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(totalPages);
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToPage = (page) => setCurrentPage(page);

    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="text-gray-400 ml-1">⇅</span>;
        }
        return sortConfig.direction === 'ascending' ?
            <span className="text-blue-600 ml-1">↑</span> :
            <span className="text-blue-600 ml-1">↓</span>;
    };

    const handleRefresh = async () => {
        toast.info('Đang tải lại dữ liệu...');
        await fetchNhanSuList();
        toast.success('Đã tải lại dữ liệu thành công!');
    };

    // Tổ là bản sao của Phân bổ nhân sự; dữ liệu cũ hoặc phân bổ mới sửa ở nơi khác thì bấm để ghi lại.
    const handleSyncTeams = async () => {
        if (isSyncingTeams) return;
        try {
            setIsSyncingTeams(true);
            const changes = await syncEmployeeTeams();
            if (changes.length) {
                toast.success(`Đã cập nhật tổ cho ${changes.length} nhân sự theo Phân bổ nhân sự`);
                await fetchNhanSuList();
            } else {
                toast.info('Tổ của nhân sự đã khớp với Phân bổ nhân sự');
            }
        } catch (error) {
            console.error('Error syncing employee teams:', error);
            toast.error('Không đồng bộ được tổ: ' + (error.message || ''));
        } finally {
            setIsSyncingTeams(false);
        }
    };

    // ==================== STATISTICS ====================

    const uniqueBoPhan = [...new Set(nhanSuList.map(ns => ns['BỘ PHẬN']).filter(Boolean))];
    // Một người phụ trách nhiều tổ thì cột Tổ ghi "V2, V11": đếm và lọc theo từng tổ.
    const uniqueTo = [...new Set(nhanSuList.flatMap(ns => splitEmployeeTeams(ns['TỔ'])))]
        .sort((a, b) => a.localeCompare(b, 'vi', { numeric: true }));
    const tongNam = nhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nam').length;
    const tongNu = nhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nữ').length;

    return (
        <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
                    {/* Header Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                                <Users className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Quản Lý Nhân Sự</h1>
                                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Quản lý thông tin nhân viên</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={handleRefresh}
                                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                                title="Tải lại dữ liệu"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span className="hidden sm:inline">Làm mới</span>
                            </button>
                            <button
                                onClick={handleSyncTeams}
                                disabled={isSyncingTeams}
                                className="px-3 py-1.5 text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm disabled:opacity-50"
                                title="Ghi lại cột Tổ theo các công đoạn mỗi người đang phụ trách trong Phân bổ nhân sự"
                            >
                                <Link2 className={`w-4 h-4 ${isSyncingTeams ? 'animate-pulse' : ''}`} />
                                <span className="hidden sm:inline">{isSyncingTeams ? 'Đang đồng bộ...' : 'Đồng bộ tổ'}</span>
                            </button>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilters
                                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <Filter className="w-4 h-4" />
                                <span className="hidden sm:inline">Bộ lọc</span>
                            </button>
                            <button
                                onClick={handleExportTemplate}
                                className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                                title="Tải file Excel mẫu"
                            >
                                <FileSpreadsheet className="w-4 h-4" />
                                <span className="hidden sm:inline">Mẫu Excel</span>
                            </button>
                            <button
                                onClick={handleExportData}
                                className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                                title="Xuất dữ liệu ra Excel"
                            >
                                <Download className="w-4 h-4" />
                                <span className="hidden sm:inline">Xuất Excel</span>
                            </button>
                            <button
                                onClick={handleOpenImportModal}
                                className="px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                                title="Import từ Excel"
                            >
                                <Upload className="w-4 h-4" />
                                <span className="hidden sm:inline">Import Excel</span>
                            </button>
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1.5 transition-all shadow-md hover:shadow-lg text-sm font-medium"
                            >
                                <Plus className="w-4 h-4" />
                                <span>Thêm nhân sự</span>
                            </button>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 mb-3">
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <Users className="w-4 h-4 text-blue-600" />
                                <p className="text-xs font-medium text-blue-700">Tổng nhân sự</p>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">
                                {filteredNhanSuList.length}
                            </p>
                            {(filterBoPhan !== 'ALL' || filterTo !== 'ALL' || filterGioiTinh !== 'ALL' || filterTrangThai !== 'ALL') && (
                                <p className="text-xs text-blue-600 mt-0.5">
                                    Tổng: {nhanSuList.length}
                                </p>
                            )}
                        </div>

                        <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-l-4 border-green-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-green-600" />
                                <p className="text-xs font-medium text-green-700">Nam</p>
                            </div>
                            <p className="text-2xl font-bold text-green-900">
                                {filteredNhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nam').length}
                            </p>
                            <p className="text-xs text-green-600 mt-0.5">
                                {((filteredNhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nam').length / filteredNhanSuList.length) * 100 || 0).toFixed(1)}%
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-3 rounded-lg border-l-4 border-pink-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-pink-600" />
                                <p className="text-xs font-medium text-pink-700">Nữ</p>
                            </div>
                            <p className="text-2xl font-bold text-pink-900">
                                {filteredNhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nữ').length}
                            </p>
                            <p className="text-xs text-pink-600 mt-0.5">
                                {((filteredNhanSuList.filter(ns => ns['GIỚI TÍNH'] === 'Nữ').length / filteredNhanSuList.length) * 100 || 0).toFixed(1)}%
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-lg border-l-4 border-purple-500 shadow-sm hover:shadow-md transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <MapPin className="w-4 h-4 text-purple-600" />
                                <p className="text-xs font-medium text-purple-700">Bộ phận</p>
                            </div>
                            <p className="text-2xl font-bold text-purple-900">
                                {uniqueBoPhan.length}
                            </p>
                            <p className="text-xs text-purple-600 mt-0.5">
                                {uniqueTo.length} tổ
                            </p>
                        </div>
                    </div>

                    {/* Filters Section */}
                    {showFilters && (
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3 animate-fadeIn">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Bộ phận</label>
                                    <select
                                        value={filterBoPhan}
                                        onChange={(e) => setFilterBoPhan(e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="ALL">Tất cả</option>
                                        {uniqueBoPhan.map((bp, index) => (
                                            <option key={index} value={bp}>{bp}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Tổ</label>
                                    <select
                                        value={filterTo}
                                        onChange={(e) => setFilterTo(e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="ALL">Tất cả</option>
                                        {uniqueTo.map((to, index) => (
                                            <option key={index} value={to}>{to}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Giới tính</label>
                                    <select
                                        value={filterGioiTinh}
                                        onChange={(e) => setFilterGioiTinh(e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="ALL">Tất cả</option>
                                        <option value="Nam">Nam</option>
                                        <option value="Nữ">Nữ</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Trạng thái làm việc</label>
                                    <select
                                        value={filterTrangThai}
                                        onChange={(e) => setFilterTrangThai(e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="ALL">Tất cả</option>
                                        {EMPLOYEE_WORK_STATUSES.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end mt-2">
                                <button
                                    onClick={() => {
                                        setFilterBoPhan('ALL');
                                        setFilterTo('ALL');
                                        setFilterGioiTinh('ALL');
                                        setFilterTrangThai('ALL');
                                    }}
                                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    Xóa bộ lọc
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Tìm kiếm theo Số HĐ, Họ tên, SĐT, CCCD..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Table Section */}
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        {isLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="relative">
                                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                                    <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-blue-300 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        ) : currentItems.length === 0 ? (
                            <div className="text-center py-12">
                                <Users className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">Không tìm thấy nhân sự nào</p>
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900 cursor-pointer hover:bg-blue-200 transition-colors"
                                            onClick={() => requestSort('SỐ HĐ')}>
                                            Số HĐ {getSortIcon('SỐ HĐ')}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900 cursor-pointer hover:bg-blue-200 transition-colors"
                                            onClick={() => requestSort('HỌ VÀ TÊN')}>
                                            Họ và Tên {getSortIcon('HỌ VÀ TÊN')}
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">
                                            Tổ
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">
                                            Bộ Phận
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">
                                            SĐT
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900 cursor-pointer hover:bg-blue-200 transition-colors"
                                            onClick={() => requestSort('NGÀY SINH')}>
                                            Ngày Sinh {getSortIcon('NGÀY SINH')}
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">
                                            Tuổi
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">
                                            Giới Tính
                                        </th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">
                                            CCCD
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">
                                            Trạng thái
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">
                                            Thao tác
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {currentItems.map((ns, index) => (
                                        <tr key={index} className="hover:bg-blue-50 transition-colors">
                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                                {ns['SỐ HĐ']}
                                            </td>
                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">
                                                {ns['HỌ VÀ TÊN']}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                {ns['TỔ'] || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                {ns['BỘ PHẬN'] || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                {ns['SỐ ĐIỆN THOẠI'] || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-center text-gray-700">
                                                {ns['NGÀY SINH'] ? formatDate(ns['NGÀY SINH']) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {calculateAge(ns['NGÀY SINH']) || '-'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ns['GIỚI TÍNH'] === 'Nam'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-pink-100 text-pink-800'
                                                    }`}>
                                                    {ns['GIỚI TÍNH']}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-700">
                                                {ns['CCCD'] || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${WORK_STATUS_BADGE[getEmployeeWorkStatus(ns)] || 'bg-gray-100 text-gray-700'}`}>
                                                    {getEmployeeWorkStatus(ns)}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => handleOpenDetailModal(ns)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Xem chi tiết"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(ns)}
                                                        className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                                                        title="Sửa"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteConfirmation(ns)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Xóa"
                                                    >
                                                        <Trash className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {filteredNhanSuList.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between mt-3 gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600">Hiển thị</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                                <span className="text-xs text-gray-600">
                                    / {filteredNhanSuList.length} nhân sự
                                </span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={goToFirstPage}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Trang đầu"
                                >
                                    <ChevronsLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Trang trước"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                <div className="flex gap-1">
                                    {getPageNumbers().map((page, index) => (
                                        page === '...' ? (
                                            <span key={index} className="px-2 py-1 text-xs text-gray-500">...</span>
                                        ) : (
                                            <button
                                                key={index}
                                                onClick={() => goToPage(page)}
                                                className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${currentPage === page
                                                        ? 'bg-blue-500 text-white font-medium'
                                                        : 'border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        )
                                    ))}
                                </div>

                                <button
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Trang sau"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={goToLastPage}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Trang cuối"
                                >
                                    <ChevronsRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Thêm/Sửa Nhân Sự */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                            <h2 className="text-lg md:text-xl font-bold text-blue-900 flex items-center gap-2">
                                <User className="w-5 h-5" />
                                {isEditMode ? 'Sửa Thông Tin Nhân Sự' : 'Thêm Nhân Sự Mới'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Thông tin cơ bản */}
                                <div className="space-y-3">
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <h3 className="font-semibold text-blue-900 mb-3 text-sm flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            Thông tin cơ bản
                                        </h3>

                                        <div className="space-y-2.5">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Số HĐ <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentNhanSu['SỐ HĐ']}
                                                    onChange={(e) => handleInputChange('SỐ HĐ', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập số hợp đồng"
                                                />
                                                {isEditMode && (
                                                    <p className="text-[11px] text-gray-500 mt-1">
                                                        Không đổi được Số HĐ nếu nhân sự đã có dữ liệu lương.
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Họ và Tên <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentNhanSu['HỌ VÀ TÊN']}
                                                    onChange={(e) => handleInputChange('HỌ VÀ TÊN', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập họ và tên"
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Tổ
                                                    </label>
                                                    {/* Tổ lấy từ Phân bổ nhân sự (công đoạn người này phụ trách), không gõ tay ở đây. */}
                                                    <input
                                                        type="text"
                                                        value={currentNhanSu['TỔ']}
                                                        disabled
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                                        placeholder="Chưa phụ trách công đoạn nào"
                                                    />
                                                    <p className="text-[11px] text-gray-500 mt-0.5">Tự lấy từ Phân bổ nhân sự</p>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Bộ Phận
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={currentNhanSu['BỘ PHẬN']}
                                                        onChange={(e) => handleInputChange('BỘ PHẬN', e.target.value)}
                                                        disabled={isSubmitting}
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                        placeholder="Nhập bộ phận"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Số Điện Thoại
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={currentNhanSu['SỐ ĐIỆN THOẠI']}
                                                    onChange={(e) => handleInputChange('SỐ ĐIỆN THOẠI', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập số điện thoại"
                                                />
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Ngày Sinh
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={currentNhanSu['NGÀY SINH']}
                                                        onChange={(e) => handleInputChange('NGÀY SINH', e.target.value)}
                                                        disabled={isSubmitting}
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Giới Tính
                                                    </label>
                                                    <select
                                                        value={currentNhanSu['GIỚI TÍNH']}
                                                        onChange={(e) => handleInputChange('GIỚI TÍNH', e.target.value)}
                                                        disabled={isSubmitting}
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                                                    >
                                                        <option value="Nam">Nam</option>
                                                        <option value="Nữ">Nữ</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Thông tin địa chỉ */}
                                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                        <h3 className="font-semibold text-green-900 mb-3 text-sm flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            Thông tin địa chỉ
                                        </h3>

                                        <div className="space-y-2.5">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Quê Quán
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentNhanSu['QUÊ QUÁN']}
                                                    onChange={(e) => handleInputChange('QUÊ QUÁN', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập quê quán"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Địa Chỉ Hiện Tại
                                                </label>
                                                <textarea
                                                    value={currentNhanSu['ĐỊA CHỈ']}
                                                    onChange={(e) => handleInputChange('ĐỊA CHỈ', e.target.value)}
                                                    disabled={isSubmitting}
                                                    rows={3}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 resize-none"
                                                    placeholder="Nhập địa chỉ hiện tại"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Thông tin CCCD */}
                                <div className="space-y-3">
                                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                        <h3 className="font-semibold text-purple-900 mb-3 text-sm flex items-center gap-2">
                                            <IdCard className="w-4 h-4" />
                                            Thông tin CCCD
                                        </h3>

                                        <div className="space-y-2.5">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Số CCCD
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentNhanSu['CCCD']}
                                                    onChange={(e) => handleInputChange('CCCD', e.target.value)}
                                                    disabled={isSubmitting}
                                                    maxLength={12}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập số CCCD (12 số)"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Ngày Cấp
                                                </label>
                                                <input
                                                    type="date"
                                                    value={currentNhanSu['NGÀY CẤP']}
                                                    onChange={(e) => handleInputChange('NGÀY CẤP', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Ngày Hết Hạn
                                                </label>
                                                <input
                                                    type="date"
                                                    value={currentNhanSu['NGÀY HẾT HẠN CCCD']}
                                                    onChange={(e) => handleInputChange('NGÀY HẾT HẠN CCCD', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Nơi Cấp
                                                </label>
                                                <input
                                                    type="text"
                                                    value={currentNhanSu['NƠI CẤP']}
                                                    onChange={(e) => handleInputChange('NƠI CẤP', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                                                    placeholder="Nhập nơi cấp CCCD"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Thông tin làm việc */}
                                    <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                                        <h3 className="font-semibold text-orange-900 mb-3 text-sm flex items-center gap-2">
                                            <Briefcase className="w-4 h-4" />
                                            Thông tin làm việc
                                        </h3>

                                        <div className="space-y-2.5">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Trạng Thái Làm Việc
                                                </label>
                                                <select
                                                    value={currentNhanSu['TRẠNG THÁI LÀM VIỆC']}
                                                    onChange={(e) => handleInputChange('TRẠNG THÁI LÀM VIỆC', e.target.value)}
                                                    disabled={isSubmitting}
                                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                                                >
                                                    {EMPLOYEE_WORK_STATUSES.map(status => (
                                                        <option key={status} value={status}>{status}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Ngày Bắt Đầu
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={currentNhanSu['NGÀY BẮT ĐẦU']}
                                                        onChange={(e) => handleInputChange('NGÀY BẮT ĐẦU', e.target.value)}
                                                        disabled={isSubmitting}
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Ngày Nghỉ {currentNhanSu['TRẠNG THÁI LÀM VIỆC'] === 'Đã nghỉ' && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={currentNhanSu['NGÀY NGHỈ']}
                                                        onChange={(e) => handleInputChange('NGÀY NGHỈ', e.target.value)}
                                                        disabled={isSubmitting}
                                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-100"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Thông tin bổ sung */}
                                    {currentNhanSu['NGÀY SINH'] && (
                                        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                            <h3 className="font-semibold text-yellow-900 mb-2 text-sm flex items-center gap-2">
                                                <Info className="w-4 h-4" />
                                                Thông tin bổ sung
                                            </h3>
                                            <div className="space-y-1.5 text-xs">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Tuổi:</span>
                                                    <span className="font-medium text-gray-900">
                                                        {calculateAge(currentNhanSu['NGÀY SINH'])} tuổi
                                                    </span>
                                                </div>
                                                {currentNhanSu['NGÀY HẾT HẠN CCCD'] && (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">CCCD còn hiệu lực:</span>
                                                        <span className={`font-medium ${new Date(currentNhanSu['NGÀY HẾT HẠN CCCD']) > new Date()
                                                                ? 'text-green-700'
                                                                : 'text-red-700'
                                                            }`}>
                                                            {new Date(currentNhanSu['NGÀY HẾT HẠN CCCD']) > new Date()
                                                                ? 'Còn hiệu lực'
                                                                : 'Hết hiệu lực'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={handleCloseModal}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleSaveNhanSu}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        {isEditMode ? 'Cập nhật' : 'Lưu'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Xem Chi Tiết */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                            <h2 className="text-lg md:text-xl font-bold text-blue-900 flex items-center gap-2">
                                <Eye className="w-5 h-5" />
                                Chi Tiết Nhân Sự - {currentNhanSu['SỐ HĐ']}
                            </h2>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="p-1.5 hover:bg-blue-200 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Thông tin cơ bản */}
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <h3 className="font-semibold text-blue-900 mb-3 text-sm flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        Thông tin cơ bản
                                    </h3>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Số HĐ:</span>
                                            <span className="font-semibold text-gray-900">{currentNhanSu['SỐ HĐ']}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Họ và Tên:</span>
                                            <span className="font-semibold text-gray-900">{currentNhanSu['HỌ VÀ TÊN']}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Tổ:</span>
                                            <span className="text-gray-900">{currentNhanSu['TỔ'] || '-'}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Bộ Phận:</span>
                                            <span className="text-gray-900">{currentNhanSu['BỘ PHẬN'] || '-'}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Số Điện Thoại:</span>
                                            <span className="text-gray-900">{currentNhanSu['SỐ ĐIỆN THOẠI'] || '-'}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Ngày Sinh:</span>
                                            <span className="text-gray-900">
                                                {currentNhanSu['NGÀY SINH'] ? formatDate(currentNhanSu['NGÀY SINH']) : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Tuổi:</span>
                                            <span className="font-semibold text-blue-700">
                                                {calculateAge(currentNhanSu['NGÀY SINH']) || '-'} tuổi
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Giới Tính:</span>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${currentNhanSu['GIỚI TÍNH'] === 'Nam'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-pink-100 text-pink-800'
                                                }`}>
                                                {currentNhanSu['GIỚI TÍNH']}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Trạng Thái:</span>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${WORK_STATUS_BADGE[getEmployeeWorkStatus(currentNhanSu)] || 'bg-gray-100 text-gray-700'}`}>
                                                {getEmployeeWorkStatus(currentNhanSu)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-blue-100">
                                            <span className="text-gray-600 font-medium">Ngày Bắt Đầu:</span>
                                            <span className="text-gray-900">
                                                {currentNhanSu['NGÀY BẮT ĐẦU'] ? formatDate(currentNhanSu['NGÀY BẮT ĐẦU']) : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5">
                                            <span className="text-gray-600 font-medium">Ngày Nghỉ:</span>
                                            <span className="text-gray-900">
                                                {currentNhanSu['NGÀY NGHỈ'] ? formatDate(currentNhanSu['NGÀY NGHỈ']) : '-'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Thông tin CCCD */}
                                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                    <h3 className="font-semibold text-purple-900 mb-3 text-sm flex items-center gap-2">
                                        <IdCard className="w-4 h-4" />
                                        Thông tin CCCD
                                    </h3>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between py-1.5 border-b border-purple-100">
                                            <span className="text-gray-600 font-medium">Số CCCD:</span>
                                            <span className="font-semibold text-gray-900">{currentNhanSu['CCCD'] || '-'}</span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-purple-100">
                                            <span className="text-gray-600 font-medium">Ngày Cấp:</span>
                                            <span className="text-gray-900">
                                                {currentNhanSu['NGÀY CẤP'] ? formatDate(currentNhanSu['NGÀY CẤP']) : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-purple-100">
                                            <span className="text-gray-600 font-medium">Ngày Hết Hạn:</span>
                                            <span className="text-gray-900">
                                                {currentNhanSu['NGÀY HẾT HẠN CCCD'] ? formatDate(currentNhanSu['NGÀY HẾT HẠN CCCD']) : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between py-1.5 border-b border-purple-100">
                                            <span className="text-gray-600 font-medium">Nơi Cấp:</span>
                                            <span className="text-gray-900">{currentNhanSu['NƠI CẤP'] || '-'}</span>
                                        </div>
                                        {currentNhanSu['NGÀY HẾT HẠN CCCD'] && (
                                            <div className="flex justify-between py-1.5">
                                                <span className="text-gray-600 font-medium">Tình trạng:</span>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${new Date(currentNhanSu['NGÀY HẾT HẠN CCCD']) > new Date()
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {new Date(currentNhanSu['NGÀY HẾT HẠN CCCD']) > new Date()
                                                        ? '✓ Còn hiệu lực'
                                                        : '✗ Hết hiệu lực'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Thông tin địa chỉ */}
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200 md:col-span-2">
                                    <h3 className="font-semibold text-green-900 mb-3 text-sm flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        Thông tin địa chỉ
                                    </h3>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between py-1.5 border-b border-green-100">
                                            <span className="text-gray-600 font-medium">Quê Quán:</span>
                                            <span className="text-gray-900">{currentNhanSu['QUÊ QUÁN'] || '-'}</span>
                                        </div>
                                        <div className="py-1.5">
                                            <span className="text-gray-600 font-medium block mb-1">Địa Chỉ Hiện Tại:</span>
                                            <p className="text-gray-900 bg-white p-2 rounded border border-green-200">
                                                {currentNhanSu['ĐỊA CHỈ'] || '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Lịch sử phân bổ công đoạn */}
                                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 md:col-span-2">
                                    <h3 className="font-semibold text-orange-900 mb-3 text-sm flex items-center gap-2">
                                        <Briefcase className="w-4 h-4" />
                                        Lịch sử phân bổ công đoạn
                                        <span className="ml-auto text-xs font-normal bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">
                                            {phanBoList.filter(pb => pb['NHÂN SỰ PHỤ TRÁCH'] === currentNhanSu['HỌ VÀ TÊN']).length} bản ghi
                                        </span>
                                    </h3>
                                    {(() => {
                                        const lichSu = phanBoList.filter(pb => pb['NHÂN SỰ PHỤ TRÁCH'] === currentNhanSu['HỌ VÀ TÊN']);
                                        if (lichSu.length === 0) {
                                            return (
                                                <p className="text-xs text-gray-500 text-center py-4">Chưa có phân bổ công đoạn nào</p>
                                            );
                                        }
                                        return (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs">
                                                    <thead className="bg-orange-100 border-b border-orange-200">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left font-semibold text-orange-900">Tổ</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-orange-900">Nhóm</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-orange-900">Mã CĐ</th>
                                                            <th className="px-3 py-2 text-left font-semibold text-orange-900">Tên Công Đoạn</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-orange-900">Hiệu lực từ</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-orange-900">Hiệu lực đến</th>
                                                            <th className="px-3 py-2 text-center font-semibold text-orange-900">Trạng thái</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-orange-100">
                                                        {lichSu.map((pb, idx) => {
                                                            const today = new Date();
                                                            const hieuLucTu = pb['HIỆU LỰC TỪ'] ? new Date(pb['HIỆU LỰC TỪ']) : null;
                                                            const hieuLucDen = pb['HIỆU LỰC ĐẾN'] ? new Date(pb['HIỆU LỰC ĐẾN']) : null;
                                                            const isActive = (!hieuLucTu || today >= hieuLucTu) && (!hieuLucDen || today <= hieuLucDen);
                                                            return (
                                                                <tr key={idx} className={`hover:bg-orange-50 transition-colors ${isActive ? 'bg-green-50' : ''}`}>
                                                                    <td className="px-3 py-2 font-medium text-gray-800">{pb['TỔ'] || '-'}</td>
                                                                    <td className="px-3 py-2 text-gray-700">{pb['NHÓM'] || '-'}</td>
                                                                    <td className="px-3 py-2 text-gray-700">{pb['MÃ CÔNG ĐOẠN'] || '-'}</td>
                                                                    <td className="px-3 py-2 text-gray-700">{pb['TÊN CÔNG ĐOẠN'] || '-'}</td>
                                                                    <td className="px-3 py-2 text-center text-gray-700">
                                                                        {pb['HIỆU LỰC TỪ'] ? new Date(pb['HIỆU LỰC TỪ']).toLocaleDateString('vi-VN') : '-'}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center text-gray-700">
                                                                        {pb['HIỆU LỰC ĐẾN'] ? new Date(pb['HIỆU LỰC ĐẾN']).toLocaleDateString('vi-VN') : 'Hiện tại'}
                                                                    </td>
                                                                    <td className="px-3 py-2 text-center">
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                                            {isActive ? 'Đang hiệu lực' : 'Hết hiệu lực'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => {
                                    setShowDetailModal(false);
                                    handleOpenModal(currentNhanSu);
                                }}
                                className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-2 transition-colors"
                            >
                                <Edit className="w-4 h-4" />
                                Sửa
                            </button>
                            <button
                                onClick={() => setShowDetailModal(false)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Import Excel */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-orange-100">
                            <h2 className="text-lg md:text-xl font-bold text-orange-900 flex items-center gap-2">
                                <Upload className="w-5 h-5" />
                                Import Dữ Liệu Nhân Sự Từ Excel
                            </h2>
                            <button
                                onClick={handleCloseImportModal}
                                className="p-1.5 hover:bg-orange-200 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            {/* Chọn file */}
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
                                <label className="block text-sm font-medium text-blue-900 mb-2">
                                    Chọn file Excel <span className="text-red-500">*</span>
                                </label>
                                <div className="flex items-center gap-3">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileSelect}
                                        className="flex-1 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"
                                    />
                                    {importFile && (
                                        <button
                                            onClick={() => {
                                                setImportFile(null);
                                                setImportPreview([]);
                                                if (fileInputRef.current) {
                                                    fileInputRef.current.value = '';
                                                }
                                            }}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Xóa file"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                {importFile && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-green-700">
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Đã chọn: {importFile.name}</span>
                                    </div>
                                )}
                            </div>

                            {/* Preview data */}
                            {importPreview.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-semibold text-gray-900 text-sm">
                                            Xem trước dữ liệu ({importPreview.length} dòng)
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                                <CheckCircle className="w-4 h-4" />
                                                {importPreview.filter(r => r.errors.length === 0).length} hợp lệ
                                            </span>
                                            <span className="text-xs text-red-600 flex items-center gap-1">
                                                <XCircle className="w-4 h-4" />
                                                {importPreview.filter(r => r.errors.length > 0).length} lỗi
                                            </span>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto rounded-lg border border-gray-300 max-h-96">
                                        <table className="w-full text-xs bg-white">
                                            <thead className="bg-gray-100 border-b border-gray-300 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">Dòng</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">Số HĐ</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">Họ Tên</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">Bộ Phận</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">SĐT</th>
                                                    <th className="px-2 py-2 text-center font-semibold text-gray-900">Giới Tính</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">CCCD</th>
                                                    <th className="px-2 py-2 text-left font-semibold text-gray-900">Trạng thái</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {importPreview.map((row, index) => (
                                                    <tr key={index} className={row.errors.length > 0 ? 'bg-red-50' : 'hover:bg-gray-50'}>
                                                        <td className="px-2 py-2 text-gray-700">{row.rowIndex}</td>
                                                        <td className="px-2 py-2 text-gray-700 font-medium">{row.soHD}</td>
                                                        <td className="px-2 py-2 text-gray-700">{row.hoTen}</td>
                                                        <td className="px-2 py-2 text-gray-700">{row.boPhan}</td>
                                                        <td className="px-2 py-2 text-gray-700">{row.sdt}</td>
                                                        <td className="px-2 py-2 text-center text-gray-700">{row.gioiTinh}</td>
                                                        <td className="px-2 py-2 text-gray-700">{row.cccd}</td>
                                                        <td className="px-2 py-2">
                                                            {row.errors.length === 0 ? (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                    Hợp lệ
                                                                </span>
                                                            ) : (
                                                                <div className="space-y-1">
                                                                    {row.errors.map((error, idx) => (
                                                                        <div key={idx} className="flex items-start gap-1 text-red-600">
                                                                            <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                            <span className="text-xs">{error}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Hướng dẫn */}
                            {importPreview.length === 0 && (
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <div className="flex items-start gap-3">
                                        <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-yellow-800">
                                            <p className="font-semibold mb-2">Hướng dẫn import:</p>
                                            <ol className="list-decimal list-inside space-y-1">
                                                <li>Tải file mẫu Excel bằng nút "Mẫu Excel"</li>
                                                <li>Điền dữ liệu vào file mẫu theo đúng định dạng</li>
                                                <li>Chọn file Excel đã điền dữ liệu</li>
                                                <li>Kiểm tra xem trước và nhấn "Import"</li>
                                            </ol>
                                            <p className="mt-2 text-xs">
                                                <strong>Lưu ý:</strong> Số HĐ không được trùng lặp. CCCD phải có 12 số. SĐT phải có 10-11 số.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={handleCloseImportModal}
                                disabled={isImporting}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={handleImportData}
                                disabled={isImporting || importPreview.length === 0 || importPreview.every(r => r.errors.length > 0)}
                                className="px-4 py-2 text-sm bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {isImporting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Đang import...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Import dữ liệu
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Xác Nhận Xóa */}
            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                                <AlertCircle className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                                Xác nhận xóa nhân sự
                            </h3>
                            <p className="text-sm text-gray-600 text-center mb-6">
                                Bạn có chắc chắn muốn xóa nhân sự <strong>{nhanSuToDelete?.['HỌ VÀ TÊN']}</strong> (Số HĐ: <strong>{nhanSuToDelete?.['SỐ HĐ']}</strong>)?
                                <br />
                                Hành động này không thể hoàn tác.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleCloseDeleteConfirmation}
                                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Hủy
                                </button>
                                <button
                                    onClick={handleDeleteNhanSu}
                                    className="flex-1 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                                >
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default NhanSuManagement;

